import React from 'react';
import { useSelector } from 'react-redux';

import { makeStyles } from 'tss-react/mui';
import { useIntl } from 'ha/i18n';
import { Grid, Paper, Typography, units, Button } from '@hbf/dsl';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { LoginKeysBold } from '@ha/icons';
import { getIsAuthenticated } from 'ha/modules/AuthLogic/selectors';

import { SearchBar } from './SearchBar';
import { Picture } from './Picture';

export const useStyles = makeStyles()(theme => ({
  container: {
    [theme.breakpoints.down('md')]: {
      backgroundColor: theme.palette.white.main,
    },
  },
  header: {
    color: theme.palette.primary.main,
    [`@supports (
          (background-clip: text) or (-webkit-background-clip: text)
        ) and (
          (text-fill-color: transparent) or (-webkit-text-fill-color: transparent)
        )`]: {
      background: 'linear-gradient(10deg, #FFA58D 0% 30%, #664B87 40% 100%);',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      textFillColor: 'transparent',
      // this prefix is not added on server side
      // https://github.com/cssinjs/jss/issues/279
      WebkitTextFillColor: 'transparent',
      [theme.breakpoints.up('md')]: {
        background: 'linear-gradient(10deg, #FFA58D 0% 20%, #664B87 40% 100%);',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        textFillColor: 'transparent',
        WebkitTextFillColor: 'transparent',
      },
      fallbacks: {
        background: theme.palette.primary.main,
      },
    },
    fontSize: units.rem(1.75),
    textAlign: 'center',
    margin: '0',
    padding: units.yx(2, 3),
    [theme.breakpoints.up('md')]: {
      fontSize: units.rem(3.25),
      lineHeight: units.rem(3.375),
      paddingTop: units.rem(3.875),
      paddingBottom: units.rem(1),
      maxWidth: units.rem(49),
    },
  },
  searchBarContainer: {
    position: 'relative',
  },
  searchBar: {
    margin: units.txb(0, 0.5, 2),
    padding: units.rem(0.5),
    [theme.breakpoints.up('md')]: {
      margin: units.txb(4, 1, 12),
      maxWidth: units.rem(70),
    },
    zIndex: 1,
  },
  paper: {
    borderRadius: 10,
  },
  noCTA: {
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(4),
      content: '""',
    },
  },
  rentOutButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(5),
    backgroundColor: theme.palette.white.main,
    '&:hover': {
      backgroundColor: theme.palette.white.main,
    },
    borderColor: theme.palette.primary.main,
    boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.3)',
  },
}));

export const Hero: React.FC = () => {
  const { classes } = useStyles();
  const { T, urlResolver } = useIntl();
  const { md } = useMediaQuery();
  const isAuthenticated: boolean = useSelector(getIsAuthenticated);

  return (
    <div className={classes.container}>
      <Grid container justifyContent="center">
        <Typography.H1
          align="center"
          className={classes.header}
          data-test-locator="Home Hero Title"
        >
          {T('Safely book your new home abroad')}
        </Typography.H1>
      </Grid>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        className={classes.searchBarContainer}
      >
        <Picture />
        <Grid item xs={12} className={classes.searchBar}>
          <Paper classes={{ root: classes.paper }}>
            <SearchBar />
          </Paper>
        </Grid>
        {!md && !isAuthenticated ? (
          <React.Fragment>
            <Grid item xs={12}>
              <Typography.H2
                align="center"
                data-test-locator="Looking for tenants"
              >
                {T('home.advertiser.title')}
              </Typography.H2>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                className={classes.rentOutButton}
                href={urlResolver.getBecomeAnAdvertiserUrl()}
                data-test-locator="Rent out your place"
                startIcon={<LoginKeysBold fontSize="inherit" />}
              >
                {T('home.advertiser.cta')}
              </Button>
            </Grid>
          </React.Fragment>
        ) : (
          <p className={classes.noCTA} />
        )}
      </Grid>
    </div>
  );
};
